import * as THREE from 'three';
import {
  IDolumGorsel,
  IDolumGorselOpts,
  IVoxelKafes,
  voxelIndeks,
  voxelMerkez,
} from './IDokumSim';
import { surfaceNets, dolulukSignedAlan } from './FikoSurfaceNets';

/**
 * Dolum/katılaşma animasyonu görseli — gerçekçi erimiş metal.
 *
 * Dolu her voxel için TEK InstancedMesh (BoxGeometry). Materyal KENDİNDEN
 * PARLAR (emissive enjeksiyonu, onBeforeCompile) → sahne ışığından bağımsız
 * akkor metal görünümü. Renk siyah-cisim (blackbody) sıcak metal rampası:
 * koyu kırmızı → kırmızı → turuncu → kehribar → beyaz-sarı.
 *
 * 'zaman' (dolum): SOĞUMA CEPHESİ — yeni dolan hücreler beyaz-sıcak, arkada
 * kalanlar soğuyup koyu turuncuya döner. İki-imleç (gosterilen/soguyan) ile
 * sadece DURUM DEĞİŞTİREN hücre güncellenir → O(değişen), kare başına tarama yok.
 * 'sicaklik' (katılaşma): değere göre statik termal harita (geç katılaşan = sıcak).
 */
export class FikoDolumGorsel implements IDolumGorsel {

  private mesh: THREE.InstancedMesh;
  private geo: THREE.BufferGeometry;
  private mat: THREE.MeshBasicMaterial;
  private kok: THREE.Object3D;
  private kafes: IVoxelKafes;
  private sonucToplamSure: number;
  private renkModu: 'zaman' | 'sicaklik';

  /** instance index → fillTime (s) / katılaşma süresi; Infinity = dolmaz */
  private fillTime: Float32Array;
  /** fillTime'a göre artan sıralı instance index dizisi (animasyon için) */
  private siraliIdx: Int32Array;
  /** instance index → dünya merkez (3'lü flat) */
  private merkezler: Float32Array;
  /** Soğumuş (taban) renk — instance başına önceden hesaplı (3'lü flat) */
  private tabanRenk: Float32Array;

  /** Şu an gösterilen instance sayısı (siraliIdx prefix'i) */
  private gosterilen = 0;
  /** Soğumuş (taban renge dönmüş) instance sayısı (gosterilen'in gerisinde) */
  private soguyan = 0;
  /** Soğuma penceresi (s) — bu süre içinde dolan hücre hâlâ "sıcak cephe" */
  private coolPencere = 1;
  private sonT = -1;

  private static readonly GIZLI = new THREE.Matrix4().makeScale(0, 0, 0);
  // Akkor cephe rengi (en yeni dolan metal — parlak kehribar, beyaza patlamaz)
  private static readonly CEPHE = new THREE.Color(0xffc266);
  private mat4 = new THREE.Matrix4();
  private vTmp = new THREE.Vector3();
  private cTmp = new THREE.Color();

  // ── Termal kare modu (gerçek sıcaklık heat-map animasyonu) ──
  private termalMod = false;
  private kareler: { sure: number; T8: Uint8Array }[] = [];
  private instanceLin: Int32Array | null = null;  // instance → voxel lineer indeks
  private instanceSay = 0;
  private sonKareIdx = -1;
  // ── Çekme boşluğu kareleri (besleyici pipe + çekme boşluğu animasyonu) ──
  private boslukKareler: { sure: number; B8: Uint8Array }[] = [];
  private boslukDurum: Uint8Array | null = null;  // instance başına: 1 = şu an boş (gizli)
  private sonBoslukIdx = -1;
  // ── Kabarcık katmanı (hapsolan hava — yükselen gaz kabarcıkları) ──
  private kabarcikMesh: THREE.InstancedMesh | null = null;
  /** Kabarcık başına: bazX,bazY,bazZ, yaricap, faz, hiz, yukselme, gorunmeT (8'li flat) */
  private kabarcikVeri: Float32Array | null = null;
  private kabarcikSay = 0;
  /** Renk parlaklığı çarpanı (opts'tan) */
  private parlaklik = 1;
  /** Cephe (en yeni dolan) rengi × parlaklık */
  private cepheRenk = new THREE.Color();
  /** Kesit (clipping) düzlemi — başta devre dışı (uzakta) */
  private kesitDuzlem = new THREE.Plane(new THREE.Vector3(1, 0, 0), 1e9);
  /** Akkor glow halesi (additive, ana mesh ile buffer paylaşır) */
  private halo: THREE.InstancedMesh | null = null;
  private glowAktif = false;
  private glowYaricap = 1;
  // ── Pürüzsüz yüzey modu (surface nets) ──
  private pruzsuzMod = false;
  private yuzeyMesh: THREE.Mesh | null = null;
  private yuzeyHucreLin: Int32Array | null = null;  // vertex → voxel lin (termal recolor)
  private yuzeyRenk: Float32Array | null = null;     // vertex color attr (termal)
  // Dolum: her karede o ana kadar dolanın yüzeyini yeniden çıkar (yükselen sıvı)
  private pfDoluluk: Uint8Array | null = null;
  private pfFillTime: Float32Array | null = null;
  private pfDims: [number, number, number] = [0, 0, 0];
  private pfOrigin = new THREE.Vector3();
  private pfHucreMm = 1;
  private pfDilAdim = 1;
  private pfMaxF = 1;
  private pfSonT = -1;
  /** Teşhis: son üretilen yüzey istatistiği (üst/alt/yan yüz) */
  yuzeyStat = '';

  constructor(opts: IDolumGorselOpts) {
    this.kok = opts.kok;
    this.kafes = opts.kafes;
    this.sonucToplamSure = opts.sonuc.toplamSure;
    this.renkModu = opts.renkModu ?? 'zaman';
    // Termal kare modu: gerçek sıcaklık alanları varsa heat-map animasyonu
    this.termalMod = !!(opts.sicaklikKareler && opts.sicaklikKareler.length);
    if (this.termalMod) this.kareler = opts.sicaklikKareler!;
    if (opts.boslukKareler?.length) this.boslukKareler = opts.boslukKareler;

    this.parlaklik = opts.parlaklik ?? 1;
    this.cepheRenk.copy(FikoDolumGorsel.CEPHE).multiplyScalar(this.parlaklik);

    // Kabarcık katmanı: DOLUM modunda hava cepleri yükselen kabarcıklarla gösterilir
    if (!this.termalMod && opts.sonuc.havaCepleri?.length) {
      this.kabarcikKur(opts.sonuc.havaCepleri, opts.kafes, opts.sonuc);
    }

    // Pürüzsüz yüzey modu → surface nets ile sürekli yüzey, voxel kodunu atla
    if (opts.pruzsuz) { this.pruzsuzMod = true; this.pruzsuzKur(opts); return; }

    const kafes = opts.kafes;
    const dolumZamani = opts.sonuc.dolumZamani;
    const [nx, ny, nz] = kafes.boyut;
    const toplam = nx * ny * nz;
    const doluSayi = kafes.doluSayi;
    const kenar = kafes.hucreMm * 0.96;
    const boyutFak = opts.boyutFaktor ?? 0.72;
    this.glowAktif = opts.glow !== false;
    this.glowYaricap = kenar * boyutFak * 1.75;

    // ── Geometri: yuvarlak erimiş-damla (komşularla örtüşür → sıvımsı) ──
    // Yarıçap = hücre · boyutFaktor (küçük=ayrık küçük voxel, büyük=örtüşen sıvı).
    // Büyük voxel sayısında perf için detay 0 (20 yüz).
    this.geo = new THREE.IcosahedronGeometry(kenar * boyutFak, doluSayi > 60000 ? 0 : 1);
    // ── Erimiş metal materyali: MeshBasicMaterial = IŞIKTAN BAĞIMSIZ.
    // KRİTİK: vertexColors=FALSE (InstancedMesh renk instanceColor ile gelir);
    // toneMapped=FALSE → ham canlı renkler (ACES ile beyaza yıkanmaz).
    this.mat = new THREE.MeshBasicMaterial({
      vertexColors: false,
      toneMapped: false,
      clippingPlanes: [this.kesitDuzlem], // kesit görünümü (başta devre dışı)
    });

    this.mesh = new THREE.InstancedMesh(this.geo, this.mat, doluSayi);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.name = 'FikoDolumGorsel';

    // ── instance verilerini topla ──────────────────────────────────────
    this.fillTime = new Float32Array(doluSayi);
    this.merkezler = new Float32Array(doluSayi * 3);
    this.tabanRenk = new Float32Array(doluSayi * 3);
    this.instanceLin = new Int32Array(doluSayi);
    const doluluk = kafes.doluluk;

    let idx = 0;
    for (let lin = 0; lin < toplam && idx < doluSayi; lin++) {
      if (doluluk[lin] !== 1) continue;
      this.fillTime[idx] = dolumZamani[lin];
      this.instanceLin[idx] = lin;
      voxelMerkez(kafes, lin, this.vTmp);
      this.merkezler[idx * 3] = this.vTmp.x;
      this.merkezler[idx * 3 + 1] = this.vTmp.y;
      this.merkezler[idx * 3 + 2] = this.vTmp.z;
      idx++;
    }
    const n = idx;
    this.instanceSay = n;

    // ── fillTime'a göre artan sıralama (animasyon prefix'i için) ───────
    this.siraliIdx = new Int32Array(n);
    for (let i = 0; i < n; i++) this.siraliIdx[i] = i;
    const ft = this.fillTime;
    Array.prototype.sort.call(this.siraliIdx, (a: number, b: number) => ft[a] - ft[b]);

    // ── Taban (soğumuş) renkleri önceden hesapla ───────────────────────
    // Varış sıcaklığı varsa GERÇEK sıcaklığa göre renklen (cold-shut = koyu);
    // yoksa dolum sırasına göre erimiş-metal rampası.
    const varis = opts.sonuc.varisSicaklik;
    let vmin = Infinity, vmax = -Infinity;
    if (varis && !this.termalMod && this.instanceLin) {
      for (let i = 0; i < n; i++) { const v = varis[this.instanceLin[i]]; if (isFinite(v)) { if (v < vmin) vmin = v; if (v > vmax) vmax = v; } }
    }
    const varisVar = !!(varis && !this.termalMod && this.instanceLin && isFinite(vmin) && vmax > vmin + 1e-3);
    let maxF = 0;
    for (let i = 0; i < n; i++) { const f = ft[i]; if (f !== Infinity && f > maxF) maxF = f; }
    if (maxF <= 0) maxF = 1;
    const griDolmaz = new THREE.Color(0x2a2d33);
    for (let i = 0; i < n; i++) {
      const f = ft[i];
      if (f === Infinity) {
        this.tabanRenk[i * 3] = griDolmaz.r; this.tabanRenk[i * 3 + 1] = griDolmaz.g; this.tabanRenk[i * 3 + 2] = griDolmaz.b;
        continue;
      }
      if (varisVar) {
        // Varış sıcaklığı: erimiş metal (soğuk=koyu kızıl → sıcak=kehribar, beyaza patlamaz)
        const v = varis![this.instanceLin![i]];
        const u = (v - vmin) / (vmax - vmin);
        this.sicakMetalRampa(0.32 + 0.46 * u, this.cTmp);
      } else if (this.renkModu === 'sicaklik') {
        const u = Math.min(1, f / maxF);
        this.sicakMetalRampa(0.18 + 0.82 * u, this.cTmp);
      } else {
        // Dolum: erimiş metal (koyu kırmızı→turuncu→kehribar, beyaz değil)
        const u = Math.min(1, f / maxF);
        this.sicakMetalRampa(0.34 + 0.44 * u, this.cTmp);
      }
      this.tabanRenk[i * 3] = this.cTmp.r * this.parlaklik;
      this.tabanRenk[i * 3 + 1] = this.cTmp.g * this.parlaklik;
      this.tabanRenk[i * 3 + 2] = this.cTmp.b * this.parlaklik;
    }

    // Soğuma penceresi: toplam sürenin ~%12'si (en az bir tık)
    this.coolPencere = Math.max(this.sonucToplamSure * 0.12, 1e-3);

    if (this.termalMod) {
      // Termal: TÜM voxeller görünür; ilk kareyle (tüm parça sıcak) renklendir
      for (let i = 0; i < n; i++) this.gorunurYap(i);
      this.mesh.instanceMatrix.needsUpdate = true;
      this.mesh.count = n;
      this.kok.add(this.mesh);
      this.kareUygula(0);
      this.haloKur();
      return;
    }

    // ── Başlangıçta tüm instance gizli + taban renkli ──────────────────
    for (let i = 0; i < n; i++) {
      this.mesh.setMatrixAt(i, FikoDolumGorsel.GIZLI);
      this.mesh.setColorAt(i, this.cTmp.setRGB(this.tabanRenk[i * 3], this.tabanRenk[i * 3 + 1], this.tabanRenk[i * 3 + 2]));
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.mesh.count = n;

    this.kok.add(this.mesh);
    this.haloKur();
  }

  /**
   * Akkor glow halesi: ana mesh'in instanceMatrix + instanceColor buffer'larını
   * PAYLAŞAN, daha büyük + additive bir InstancedMesh. Sıfır ek güncelleme maliyeti
   * (buffer paylaşımı) → bloom benzeri parıltı. Performans için çok büyük sayıda atla.
   */
  private haloKur(): void {
    if (!this.glowAktif || this.instanceSay === 0 || this.instanceSay > 160000) return;
    if (!this.mesh.instanceColor) return;
    const haloGeo = new THREE.IcosahedronGeometry(this.glowYaricap, 0);
    const haloMat = new THREE.MeshBasicMaterial({
      transparent: true, opacity: 0.2, blending: THREE.AdditiveBlending,
      depthWrite: false, toneMapped: false, clippingPlanes: [this.kesitDuzlem],
    });
    const halo = new THREE.InstancedMesh(haloGeo, haloMat, this.instanceSay);
    halo.instanceMatrix = this.mesh.instanceMatrix;   // paylaşımlı (aynı konum/ölçek)
    halo.instanceColor = this.mesh.instanceColor;     // paylaşımlı (aynı renk)
    halo.count = this.mesh.count;
    halo.frustumCulled = false;
    halo.name = 'FikoDolumGlow';
    this.halo = halo;
    this.kok.add(halo);
  }

  /**
   * Termal (demir/heat-map) renk rampası. u∈[0,1] normalize sıcaklık:
   * 0 koyu (katı/soğuk) → 0.25 koyu kırmızı → 0.45 kırmızı → 0.65 turuncu
   * → 0.82 sarı → 1 beyaz-sıcak. Katı (düşük) koyu kalır → soğuma dalgası okunur.
   */
  private termalRampa(u: number, out: THREE.Color): void {
    const d: [number, number, number, number][] = [
      [0.00, 0x10, 0x12, 0x18],
      [0.18, 0x3a, 0x10, 0x28],
      [0.30, 0x90, 0x12, 0x10],
      [0.48, 0xe8, 0x33, 0x00],
      [0.66, 0xff, 0x7a, 0x00],
      [0.84, 0xff, 0xd0, 0x33],
      [1.00, 0xff, 0xf6, 0xe0],
    ];
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    for (let i = 1; i < d.length; i++) {
      if (u <= d[i][0]) {
        const a = d[i - 1], b = d[i];
        const f = (u - a[0]) / Math.max(b[0] - a[0], 1e-6);
        out.setRGB((a[1] + (b[1] - a[1]) * f) / 255, (a[2] + (b[2] - a[2]) * f) / 255, (a[3] + (b[3] - a[3]) * f) / 255);
        return;
      }
    }
    out.setRGB(d[d.length - 1][1] / 255, d[d.length - 1][2] / 255, d[d.length - 1][3] / 255);
  }

  /** Kareyi voxellere uygula (her instance gerçek sıcaklığına göre renklenir). */
  private kareUygula(kareIdx: number): void {
    if (!this.instanceLin || !this.kareler.length) return;
    const ki = Math.min(this.kareler.length - 1, Math.max(0, kareIdx));
    if (ki === this.sonKareIdx) return;
    this.sonKareIdx = ki;
    const T8 = this.kareler[ki].T8;
    const lin = this.instanceLin;
    const p = this.parlaklik;
    for (let i = 0; i < this.instanceSay; i++) {
      this.termalRampa(T8[lin[i]] / 255, this.cTmp);
      if (p !== 1) this.cTmp.multiplyScalar(p);
      this.mesh.setColorAt(i, this.cTmp);
    }
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  /**
   * Siyah-cisim (sıcak metal) renk rampası. t∈[0,1]:
   * 0 koyu kırmızı → 0.35 kırmızı → 0.58 turuncu → 0.8 kehribar → 1 beyaz-sarı.
   */
  private sicakMetalRampa(t: number, out: THREE.Color): void {
    const duraklar: [number, number, number, number][] = [
      [0.00, 0x44, 0x0c, 0x00],
      [0.30, 0xb0, 0x1c, 0x00],
      [0.55, 0xff, 0x5a, 0x00],
      [0.78, 0xff, 0xb1, 0x33],
      [1.00, 0xff, 0xf0, 0xc0],
    ];
    t = Math.min(1, Math.max(0, t));
    for (let i = 1; i < duraklar.length; i++) {
      if (t <= duraklar[i][0]) {
        const a = duraklar[i - 1], b = duraklar[i];
        const f = (t - a[0]) / Math.max(b[0] - a[0], 1e-6);
        out.setRGB(
          (a[1] + (b[1] - a[1]) * f) / 255,
          (a[2] + (b[2] - a[2]) * f) / 255,
          (a[3] + (b[3] - a[3]) * f) / 255,
        );
        return;
      }
    }
    out.setRGB(duraklar[4][1] / 255, duraklar[4][2] / 255, duraklar[4][3] / 255);
  }

  // ─────────────────────────────────────────────────────────────────────
  // t anına kadar dolan hücreleri göster + soğuma cephesi renklendir.
  // İki imleç: gosterilen (görünür prefix) ve soguyan (taban renge dönen prefix).
  // ─────────────────────────────────────────────────────────────────────
  zamaniAyarla(t: number): void {
    if (t === this.sonT) return;

    // ── Pürüzsüz yüzey modu ──
    if (this.pruzsuzMod) {
      this.sonT = t;
      if (this.termalMod && this.yuzeyMesh && this.yuzeyHucreLin && this.yuzeyRenk && this.kareler.length) {
        // Katılaşma: zamana en yakın kareyle per-vertex sıcaklık recolor (kare değişince)
        let lo = 0, hi = this.kareler.length - 1, idx = 0;
        while (lo <= hi) { const mid = (lo + hi) >> 1; if (this.kareler[mid].sure <= t) { idx = mid; lo = mid + 1; } else hi = mid - 1; }
        if (idx !== this.sonKareIdx) {
          this.sonKareIdx = idx;
          const T8 = this.kareler[idx].T8, lin = this.yuzeyHucreLin, renk = this.yuzeyRenk, p = this.parlaklik;
          for (let i = 0; i < lin.length; i++) {
            this.termalRampa(T8[lin[i]] / 255, this.cTmp);
            renk[i * 3] = this.cTmp.r * p; renk[i * 3 + 1] = this.cTmp.g * p; renk[i * 3 + 2] = this.cTmp.b * p;
          }
          const attr = (this.yuzeyMesh.geometry as THREE.BufferGeometry).getAttribute('color') as THREE.BufferAttribute;
          attr.needsUpdate = true;
        }
      } else {
        // Dolum: YÜKSELEN SIVI — o ana kadar dolanın yüzeyini yeniden çıkar (throttle)
        const esik = Math.max(this.sonucToplamSure / 50, 1e-3);
        if (this.pfSonT < 0 || Math.abs(t - this.pfSonT) >= esik || t >= this.sonucToplamSure) {
          this.pfSonT = t;
          this.pfYenidenMeshle(t);
        }
        this.kabarcikGuncelle(t);
      }
      return;
    }

    // ── Termal kare modu: zamana en yakın sıcaklık karesini uygula ──
    if (this.termalMod) {
      this.sonT = t;
      const k = this.kareler;
      // İkili arama: sure ≤ t olan son kare
      let lo = 0, hi = k.length - 1, idx = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (k[mid].sure <= t) { idx = mid; lo = mid + 1; } else hi = mid - 1;
      }
      this.kareUygula(idx);
      this.boslukUygula(t);
      return;
    }

    const sirali = this.siraliIdx;
    const ft = this.fillTime;
    const n = sirali.length;
    const cepheModu = this.renkModu === 'zaman';
    let renkDegisti = false;

    // 1) Görünür prefix hedefi (fillTime ≤ t)
    let hedefG = 0;
    while (hedefG < n) { const f = ft[sirali[hedefG]]; if (f === Infinity || f > t) break; hedefG++; }

    if (hedefG > this.gosterilen) {
      for (let p = this.gosterilen; p < hedefG; p++) {
        const i = sirali[p];
        this.gorunurYap(i);
        if (cepheModu) { this.mesh.setColorAt(i, this.cepheRenk); renkDegisti = true; } // yeni dolan = akkor cephe
      }
    } else if (hedefG < this.gosterilen) {
      for (let p = hedefG; p < this.gosterilen; p++) this.mesh.setMatrixAt(sirali[p], FikoDolumGorsel.GIZLI);
    }
    this.gosterilen = hedefG;

    // 2) Soğuma cephesi: fillTime ≤ t − pencere olan görünür hücreler taban renge döner
    if (cepheModu) {
      const esik = t - this.coolPencere;
      let hedefS = 0;
      while (hedefS < this.gosterilen) { const f = ft[sirali[hedefS]]; if (f === Infinity || f > esik) break; hedefS++; }

      if (hedefS > this.soguyan) {
        for (let p = this.soguyan; p < hedefS; p++) {
          const i = sirali[p];
          this.mesh.setColorAt(i, this.cTmp.setRGB(this.tabanRenk[i * 3], this.tabanRenk[i * 3 + 1], this.tabanRenk[i * 3 + 2]));
        }
        renkDegisti = true;
      } else if (hedefS < this.soguyan) {
        // Geri sarma: tekrar sıcak cepheye giren hücreler akkor olur
        for (let p = hedefS; p < this.soguyan; p++) this.mesh.setColorAt(sirali[p], this.cepheRenk);
        renkDegisti = true;
      }
      this.soguyan = hedefS;
    }

    this.sonT = t;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (renkDegisti && this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.kabarcikGuncelle(t);
  }

  private gorunurYap(i: number): void {
    this.mat4.makeScale(1, 1, 1);
    this.mat4.setPosition(this.merkezler[i * 3], this.merkezler[i * 3 + 1], this.merkezler[i * 3 + 2]);
    this.mesh.setMatrixAt(i, this.mat4);
  }

  /**
   * Kabarcık katmanı — hapsolan hava ceplerinde YÜKSELEN gaz kabarcıkları.
   * Metal cephesi cebe ulaştığında kabarcıklar alttan doğar, sallanarak yükselir,
   * cebin tavanında toplanır (MAGMA air-entrapment görselinin canlı hali).
   * Deterministik (index-hash faz) → timeline ileri/geri sarma tutarlı.
   */
  private kabarcikKur(
    cepler: { merkez: THREE.Vector3; hucreSayisi: number }[],
    kafes: IVoxelKafes,
    sonuc: { dolumZamani: Float32Array; toplamSure: number },
  ): void {
    const h = kafes.hucreMm;
    // Cep başına kabarcık sayısı ~ büyüklüğün karekökü (görsel yoğunluk dengesi)
    const adetler = cepler.map(c => Math.min(24, Math.max(4, Math.round(Math.sqrt(c.hucreSayisi) * 2.5))));
    const toplamKab = Math.min(240, adetler.reduce((a, b) => a + b, 0));
    if (toplamKab === 0) return;

    const geo = new THREE.SphereGeometry(1, 8, 6);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xc084fc, transparent: true, opacity: 0.75,
      toneMapped: false, depthWrite: false,
      clippingPlanes: [this.kesitDuzlem],
    });
    const mesh = new THREE.InstancedMesh(geo, mat, toplamKab);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.name = 'FikoDolumKabarcik';

    // Cebin görünme zamanı: cep merkezi çevresindeki EN ERKEN dolum zamanı
    // (metal cebe ulaşınca kabarcıklar başlar). Bulunamazsa sürenin %40'ı.
    const gorunmeT = (merkez: THREE.Vector3): number => {
      const lin = voxelIndeks(kafes, merkez);
      const [nx, ny] = kafes.boyut;
      let enErken = Infinity;
      if (lin >= 0) {
        for (const d of [0, 1, -1, nx, -nx, nx * ny, -(nx * ny)]) {
          const l = lin + d;
          if (l >= 0 && l < sonuc.dolumZamani.length) {
            const f = sonuc.dolumZamani[l];
            if (isFinite(f) && f < enErken) enErken = f;
          }
        }
      }
      return isFinite(enErken) ? enErken : sonuc.toplamSure * 0.4;
    };
    // Deterministik hash (0-1) — Math.random yok, geri sarma tutarlı
    const hash = (n: number) => {
      const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
      return s - Math.floor(s);
    };

    this.kabarcikVeri = new Float32Array(toplamKab * 8);
    let ki = 0;
    for (let c = 0; c < cepler.length && ki < toplamKab; c++) {
      const cep = cepler[c];
      const gt = gorunmeT(cep.merkez);
      const yayilim = Math.max(h, Math.sqrt(cep.hucreSayisi) * h * 0.45);
      for (let b = 0; b < adetler[c] && ki < toplamKab; b++, ki++) {
        const o = ki * 8;
        const a1 = hash(ki * 3 + 1), a2 = hash(ki * 3 + 2), a3 = hash(ki * 3 + 3);
        this.kabarcikVeri[o] = cep.merkez.x + (a1 - 0.5) * yayilim;       // bazX
        this.kabarcikVeri[o + 1] = cep.merkez.y - h * (0.5 + a2);          // bazY (altından doğar)
        this.kabarcikVeri[o + 2] = cep.merkez.z + (a3 - 0.5) * yayilim;    // bazZ
        this.kabarcikVeri[o + 3] = h * (0.10 + 0.16 * hash(ki + 7));       // yarıçap
        this.kabarcikVeri[o + 4] = hash(ki + 13);                          // faz 0-1
        this.kabarcikVeri[o + 5] = 0.5 + hash(ki + 17);                    // hız çarpanı
        this.kabarcikVeri[o + 6] = h * (1.2 + 1.8 * hash(ki + 23));        // yükselme mesafesi
        this.kabarcikVeri[o + 7] = gt;                                     // görünme zamanı (s)
      }
    }
    this.kabarcikSay = ki;
    // Başta hepsi gizli
    for (let i = 0; i < toplamKab; i++) mesh.setMatrixAt(i, FikoDolumGorsel.GIZLI);
    mesh.instanceMatrix.needsUpdate = true;
    this.kabarcikMesh = mesh;
    this.kok.add(mesh);
  }

  /** Kabarcık animasyonu: t anına göre konum/ölçek (deterministik, scrub uyumlu). */
  private kabarcikGuncelle(t: number): void {
    const mesh = this.kabarcikMesh, veri = this.kabarcikVeri;
    if (!mesh || !veri) return;
    const donem = Math.max(this.sonucToplamSure * 0.25, 0.5); // bir yükselme turu (s)
    for (let i = 0; i < this.kabarcikSay; i++) {
      const o = i * 8;
      const gt = veri[o + 7];
      if (t < gt) { mesh.setMatrixAt(i, FikoDolumGorsel.GIZLI); continue; }
      const yas = (t - gt) * veri[o + 5];
      const u = ((yas / donem) + veri[o + 4]) % 1;            // 0→1 yükselme döngüsü
      const r = veri[o + 3] * (0.6 + 0.5 * u);                 // yükselirken büyür (basınç ↓)
      const sallanma = Math.sin((u * 6.28318) * 3 + veri[o + 4] * 6.28318) * r * 0.8;
      this.mat4.makeScale(r, r, r);
      this.mat4.setPosition(
        veri[o] + sallanma,
        veri[o + 1] + veri[o + 6] * u,
        veri[o + 2] + Math.cos((u * 6.28318) * 2.3 + veri[o + 4] * 9) * r * 0.6,
      );
      mesh.setMatrixAt(i, this.mat4);
    }
    mesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * Çekme boşluğu karesini uygula: boşalan voxel KÜÇÜLÜR/KAYBOLUR →
   * besleyicideki metal seviyesinin düşüşü (pipe) ve parçadaki çekme boşluğu
   * animasyonda gözle görülür. Sadece durumu değişen instance güncellenir.
   */
  private boslukUygula(t: number): void {
    if (!this.boslukKareler.length || !this.instanceLin) return;
    const bk = this.boslukKareler;
    let lo = 0, hi = bk.length - 1, idx = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (bk[mid].sure <= t) { idx = mid; lo = mid + 1; } else hi = mid - 1;
    }
    if (idx === this.sonBoslukIdx) return;
    this.sonBoslukIdx = idx;
    if (!this.boslukDurum) this.boslukDurum = new Uint8Array(this.instanceSay);
    const durum = this.boslukDurum;
    const B8 = idx >= 0 ? bk[idx].B8 : null;
    const lin = this.instanceLin;
    let matDegisti = false;
    for (let i = 0; i < this.instanceSay; i++) {
      const v8 = B8 ? B8[lin[i]] : 0;
      // 8 seviyeli ölçek: 0 = dolu, 7 = tamamen boş (gizli)
      const seviye = v8 <= 16 ? 0 : v8 >= 240 ? 7 : (1 + ((v8 - 16) / 32) | 0);
      if (seviye === durum[i]) continue;
      durum[i] = seviye;
      matDegisti = true;
      if (seviye >= 7) {
        this.mesh.setMatrixAt(i, FikoDolumGorsel.GIZLI);
      } else {
        // Kalan metal oranına göre küçült (hacim → kenar: küpkök)
        const s = seviye === 0 ? 1 : Math.cbrt(1 - (seviye / 7) * 0.9);
        this.mat4.makeScale(s, s, s);
        this.mat4.setPosition(this.merkezler[i * 3], this.merkezler[i * 3 + 1], this.merkezler[i * 3 + 2]);
        this.mesh.setMatrixAt(i, this.mat4);
      }
    }
    if (matDegisti) this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Kesit (clipping) düzlemi: eksen yönünde oran(0-1) konumundan kes; null = kapalı. */
  kesitAyarla(eksen: 'x' | 'y' | 'z' | null, oran: number): void {
    if (!eksen) { this.kesitDuzlem.constant = 1e9; return; }
    const [nx, ny, nz] = this.kafes.boyut;
    const h = this.kafes.hucreMm, o = this.kafes.origin;
    let minV: number, maxV: number;
    if (eksen === 'x') { this.kesitDuzlem.normal.set(1, 0, 0); minV = o.x; maxV = o.x + nx * h; }
    else if (eksen === 'y') { this.kesitDuzlem.normal.set(0, 1, 0); minV = o.y; maxV = o.y + ny * h; }
    else { this.kesitDuzlem.normal.set(0, 0, 1); minV = o.z; maxV = o.z + nz * h; }
    const kes = minV + (maxV - minV) * Math.min(1, Math.max(0, oran));
    this.kesitDuzlem.constant = -kes; // kes'in pozitif tarafı görünür (iç kesit açığa çıkar)
  }

  /**
   * Pürüzsüz yüzey (surface nets).
   *  • DOLUM: her karede O ANA KADAR DOLAN bölgenin yüzeyini yeniden çıkarır
   *    → su gibi yükselen sıvı seviyesi (zamaniAyarla → pfYenidenMeshle).
   *  • KATILAŞMA: parça tamamen dolu → statik yüzey + per-vertex sıcaklık recolor.
   */
  private pruzsuzKur(opts: IDolumGorselOpts): void {
    const kafes = opts.kafes;
    const dims = kafes.boyut as [number, number, number];
    const dilAdim = Math.max(1, Math.round(opts.pruzsuzKalinlik ?? 1));

    // Kendinden aydınlatmalı materyal (karanlıkta parlar) + kesit
    const mat = new THREE.MeshBasicMaterial({
      vertexColors: true, toneMapped: false, side: THREE.DoubleSide,
      clippingPlanes: [this.kesitDuzlem],
    });
    this.yuzeyMesh = new THREE.Mesh(new THREE.BufferGeometry(), mat);
    this.yuzeyMesh.frustumCulled = false;
    this.yuzeyMesh.name = 'FikoDolumYuzey';
    this.kok.add(this.yuzeyMesh);

    if (this.termalMod) {
      // KATILAŞMA: parça tamamen dolu → statik tam yüzey, sonra sıcaklık recolor
      this.pruzsuzStatikKur(kafes, dims, dilAdim, opts.sonuc.dolumZamani);
      return;
    }

    // DOLUM: per-frame yeniden mesh için veriyi sakla, ilk kareyi çiz
    this.pfDoluluk = kafes.doluluk;
    this.pfFillTime = opts.sonuc.dolumZamani;
    this.pfDims = dims;
    this.pfOrigin.copy(kafes.origin);
    this.pfHucreMm = kafes.hucreMm;
    this.pfDilAdim = dilAdim;
    let mx = 0;
    for (let i = 0; i < this.pfFillTime.length; i++) { const f = this.pfFillTime[i]; if (isFinite(f) && f > mx) mx = f; }
    this.pfMaxF = mx > 0 ? mx : 1;
    this.pfYenidenMeshle(0);
  }

  /** Katılaşma: tam parça yüzeyi + per-vertex en yakın voxel (sıcaklık recolor için). */
  private pruzsuzStatikKur(kafes: IVoxelKafes, dims: [number, number, number], dilAdim: number, dolumZamani: Float32Array): void {
    const field = dolulukSignedAlan(kafes.doluluk, dims, dilAdim);
    const sn = surfaceNets(field, dims, kafes.origin, kafes.hucreMm);
    if (sn.indices.length === 0) return;
    const vSay = sn.positions.length / 3;
    const linDuz = this.linDuzelt(sn.hucreLin, kafes.doluluk, dims, dilAdim + 1);
    this.yuzeyHucreLin = linDuz;
    const renk = new Float32Array(vSay * 3);
    this.yuzeyRenk = renk;
    let maxF = 0;
    for (let i = 0; i < vSay; i++) { const f = dolumZamani[linDuz[i]]; if (isFinite(f) && f > maxF) maxF = f; }
    if (maxF <= 0) maxF = 1;
    for (let i = 0; i < vSay; i++) {
      const f = dolumZamani[linDuz[i]];
      const u = isFinite(f) ? Math.min(1, f / maxF) : 0;
      this.sicakMetalRampa(0.18 + 0.82 * u, this.cTmp);
      renk[i * 3] = this.cTmp.r * this.parlaklik; renk[i * 3 + 1] = this.cTmp.g * this.parlaklik; renk[i * 3 + 2] = this.cTmp.b * this.parlaklik;
    }
    const geo = this.yuzeyMesh!.geometry as THREE.BufferGeometry;
    geo.setAttribute('position', new THREE.BufferAttribute(sn.positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(renk, 3));
    geo.setIndex(new THREE.BufferAttribute(sn.indices, 1));
    geo.computeVertexNormals();
    this.yuzeyStat = this.istatistikHesapla(sn.positions, sn.indices);
  }

  /** Teşhis: yüzey normal yönlerine göre üst(+z)/alt(−z)/yan yüz sayıları. */
  private istatistikHesapla(pos: Float32Array, idx: Uint32Array): string {
    const fSay = idx.length / 3;
    let ust = 0, alt = 0, yan = 0;
    for (let f = 0; f < fSay; f++) {
      const a = idx[f * 3] * 3, b = idx[f * 3 + 1] * 3, c = idx[f * 3 + 2] * 3;
      const ux = pos[b] - pos[a], uy = pos[b + 1] - pos[a + 1], uz = pos[b + 2] - pos[a + 2];
      const vx = pos[c] - pos[a], vy = pos[c + 1] - pos[a + 1], vz = pos[c + 2] - pos[a + 2];
      const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      const nl = Math.hypot(nx, ny, nz) || 1; const nzn = nz / nl;
      if (nzn > 0.5) ust++; else if (nzn < -0.5) alt++; else yan++;
    }
    return `${pos.length / 3} vertex, ${fSay} üçgen (üst ${ust}, alt ${alt}, yan ${yan})`;
  }

  /** Genişletilen vertex'i en yakın DOLU voxele bağla (renk örnekleme). */
  private linDuzelt(hucreLin: Int32Array, dol: Uint8Array, dims: [number, number, number], aramaR: number): Int32Array {
    const [nx, ny, nz] = dims; const nxy = nx * ny;
    const out = new Int32Array(hucreLin.length);
    for (let i = 0; i < hucreLin.length; i++) {
      let lin = hucreLin[i];
      if (dol[lin] !== 1) {
        const x0 = lin % nx, y0 = ((lin / nx) | 0) % ny, z0 = (lin / nxy) | 0;
        let bulundu = -1;
        for (let r = 1; r <= aramaR && bulundu < 0; r++) {
          for (let dz = -r; dz <= r && bulundu < 0; dz++) {
            const z = z0 + dz; if (z < 0 || z >= nz) continue;
            for (let dy = -r; dy <= r && bulundu < 0; dy++) {
              const y = y0 + dy; if (y < 0 || y >= ny) continue;
              for (let dx = -r; dx <= r; dx++) {
                const x = x0 + dx; if (x < 0 || x >= nx) continue;
                const l = x + nx * (y + ny * z);
                if (dol[l] === 1) { bulundu = l; break; }
              }
            }
          }
        }
        if (bulundu >= 0) lin = bulundu;
      }
      out[i] = lin;
    }
    return out;
  }

  /**
   * DOLUM su animasyonu: o ana kadar dolan (fillTime ≤ t) bölgenin yüzeyini
   * yeniden çıkar → yükselen sıvı. Renk: yeni dolan (cephe) beyaz-sıcak, eski turuncu.
   */
  private pfYenidenMeshle(t: number): void {
    if (!this.yuzeyMesh || !this.pfDoluluk || !this.pfFillTime) return;
    const dims = this.pfDims; const [nx, ny, nz] = dims; const toplam = nx * ny * nz;
    const dol = this.pfDoluluk, ft = this.pfFillTime;
    // O ana kadar dolan voxeller
    const dolanSimdi = new Uint8Array(toplam);
    let varMi = false;
    for (let i = 0; i < toplam; i++) {
      if (dol[i] === 1 && ft[i] <= t) { dolanSimdi[i] = 1; varMi = true; }
    }
    const geo = this.yuzeyMesh.geometry as THREE.BufferGeometry;
    if (!varMi) { geo.setIndex(new THREE.BufferAttribute(new Uint32Array(0), 1)); geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(0), 3)); return; }

    const field = dolulukSignedAlan(dolanSimdi, dims, this.pfDilAdim);
    const sn = surfaceNets(field, dims, this.pfOrigin, this.pfHucreMm);
    if (sn.indices.length === 0) return;

    const vSay = sn.positions.length / 3;
    const linDuz = this.linDuzelt(sn.hucreLin, dolanSimdi, dims, this.pfDilAdim + 1);
    const renk = new Float32Array(vSay * 3);
    const cepheBand = this.pfMaxF * 0.12; // son dolan = sıcak cephe
    for (let i = 0; i < vSay; i++) {
      const f = ft[linDuz[i]];
      // cephe (t'ye yakın dolan) beyaz-sıcak; geride kalan turuncu-kehribar
      let u: number;
      if (!isFinite(f)) u = 0.5;
      else { const yas = t - f; u = yas < cepheBand ? 1 - 0.45 * (yas / Math.max(cepheBand, 1e-3)) : 0.45 * Math.max(0, 1 - (f / this.pfMaxF)); }
      this.sicakMetalRampa(0.4 + 0.55 * Math.min(1, Math.max(0, u)), this.cTmp);
      renk[i * 3] = this.cTmp.r * this.parlaklik; renk[i * 3 + 1] = this.cTmp.g * this.parlaklik; renk[i * 3 + 2] = this.cTmp.b * this.parlaklik;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(sn.positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(renk, 3));
    geo.setIndex(new THREE.BufferAttribute(sn.indices, 1));
    geo.deleteAttribute('normal');
    geo.computeVertexNormals();
  }

  sure(): number {
    return this.sonucToplamSure;
  }

  dispose(): void {
    // Kabarcık katmanı
    if (this.kabarcikMesh) {
      this.kabarcikMesh.parent?.remove(this.kabarcikMesh);
      try { (this.kabarcikMesh.geometry as THREE.BufferGeometry).dispose(); (this.kabarcikMesh.material as THREE.Material).dispose(); this.kabarcikMesh.dispose(); } catch {}
      this.kabarcikMesh = null;
    }
    // Pürüzsüz yüzey modu
    if (this.yuzeyMesh) {
      this.yuzeyMesh.parent?.remove(this.yuzeyMesh);
      try { (this.yuzeyMesh.geometry as THREE.BufferGeometry).dispose(); (this.yuzeyMesh.material as THREE.Material).dispose(); } catch {}
      this.yuzeyMesh = null;
      return;
    }
    // Halo: paylaşımlı instanceMatrix/instanceColor'ı BOZMA — sadece kendi geo+mat.
    if (this.halo) {
      this.halo.parent?.remove(this.halo);
      try { (this.halo.geometry as THREE.BufferGeometry).dispose(); (this.halo.material as THREE.Material).dispose(); } catch {}
      this.halo = null;
    }
    if (this.mesh.parent) this.mesh.parent.remove(this.mesh);
    else this.kok.remove(this.mesh);
    this.geo.dispose();
    this.mat.dispose();
    this.mesh.dispose();
  }
}
